import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './ThemeProvider';
import type { ThemeMode } from './theme';

const OPTIONS = [
  { mode: 'light', labelKey: 'theme.light', Icon: Sun },
  { mode: 'dark', labelKey: 'theme.dark', Icon: Moon },
  { mode: 'system', labelKey: 'theme.system', Icon: Monitor },
] as const satisfies ReadonlyArray<{ mode: ThemeMode; labelKey: string; Icon: typeof Sun }>;

/**
 * Segment sizes: 30px inside the header's 36px control (Brand Kit § CTA And Buttons, 36px), 44px
 * where the control is touched rather than clicked — the drawer's preferences.
 */
const SEGMENT_SIZE = { default: 'size-[30px]', touch: 'size-11' } as const;

/**
 * Compact three-state theme control: one icon per mode (light/dark/auto), no text labels — the
 * three modes stay visible at a glance in ~1/3 of the previous footprint. The mode name rides on
 * each segment's `title`/`aria-label` (tooltip + screen-reader) instead of visible prose, and the
 * active segment is marked both semantically (`aria-pressed`) and visually.
 *
 * A SEGMENTED CONTROL IN THE BRAND KIT'S SHAPE: a `--subtle` track, and the active segment raised on
 * the surface with its border and a level-1 shadow. It used to be filled with the primary colour,
 * which is now the action colour, and the kit gives that to the one primary action per view —
 * never to a preference.
 */
export function ThemeToggle({ size = 'default' }: { size?: keyof typeof SEGMENT_SIZE } = {}) {
  const { mode, setMode } = useTheme();
  const { t } = useTranslation('shell');

  return (
    <div
      className="bg-muted inline-flex items-center gap-0.5 rounded-md p-[3px]"
      role="group"
      aria-label={t('theme.groupLabel')}
    >
      {OPTIONS.map(({ mode: optionMode, labelKey, Icon }) => {
        const label = t(labelKey);
        const active = mode === optionMode;
        return (
          <button
            key={optionMode}
            type="button"
            aria-pressed={active}
            aria-label={label}
            title={label}
            onClick={() => setMode(optionMode)}
            className={`focus-visible:ring-ring inline-flex items-center justify-center rounded-sm
              border transition-colors focus-visible:ring-2 focus-visible:outline-none
              [&_svg]:size-4 ${SEGMENT_SIZE[size]} ${
                active
                  ? 'border-chip-border bg-card text-foreground shadow-[var(--shadow-1)]'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              }`}
          >
            <Icon aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
